/**
 * InspectionExternalLinkBar - Link externo da vistoria no modal interno
 *
 * Faixa fixa logo abaixo do cabeçalho, no mesmo formato da Proposta: campo somente
 * leitura com o link completo, Copiar, Abrir e um menu "Ações" exclusivo do link
 * (Compartilhar, WhatsApp, Regerar, Revogar). O acesso confirma CPF/CNPJ do
 * proprietário ou e-mail do usuário responsável, conforme o vínculo congelado.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-03 v1.3.1 - Exibe o status final real (aprovada, reprovada ou cancelada)
 *            no motivo de encerramento do link externo.
 *   RELEASE: 2026-09-28 v1.3.0 - Identidade dinâmica por documento/e-mail e bloqueio sem responsável válido.
 *   RELEASE: 2026-09-28 v1.2.0 - Faixa visível em todos os status; modo leitura em revisão;
 *            aviso de link encerrado em vistorias finalizadas; estado de erro com "Tentar novamente".
 *   RELEASE: 2026-09-27 v1.1.0 - Layout igual à faixa da Proposta; menu Ações na própria faixa;
 *            confirmação para Regerar/Revogar; esqueleto durante o carregamento.
 *   RELEASE: 2026-09-27 v1.0.0 - Criação inicial.
 */
import { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Ban, Check, Copy, ExternalLink, Link2, Loader2, MessageCircle, MoreHorizontal, RefreshCw, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buildPublicChecklistUrl, useChecklistPublicLink } from '@/hooks/useChecklistPublicLink';
import { INSPECTION_STATUS, type InspectionStatus } from '@/hooks/useVehicleInspections';

interface Props {
  instanceId: string;
  code: number;
  /** Status atual da vistoria: define se o link pode ser gerado/gerido. */
  status: string;
  /** Permissão para gerar/revogar (editar vistorias). */
  canManage: boolean;
}

/** Status em que a vistoria ainda pode ser executada pelo link. */
const EXECUTABLE = ['pending', 'in_progress', 'correction_requested'];
/** Status finais: link encerrado. */
const FINAL = ['approved', 'rejected', 'cancelled'];

export function InspectionExternalLinkBar({ instanceId, code, status, canManage: canManagePerm }: Props) {
  const isFinal = FINAL.includes(status);
  const executable = EXECUTABLE.includes(status);
  // Gerar/Regerar/Revogar só quando a vistoria ainda pode ser executada
  const canManage = canManagePerm && executable;
  const { data, isLoading, isError, refetch, generate, revoke } = useChecklistPublicLink(instanceId, !isFinal);
  const [copied, setCopied] = useState(false);
  const [confirm, setConfirm] = useState<'regen' | 'revoke' | null>(null);
  const barCls = 'px-4 md:px-5 py-2.5 bg-muted/40 border-b shrink-0';

  // Vistoria finalizada: não consulta o link, apenas informa
  if (isFinal) {
    const finalStatusLabel = INSPECTION_STATUS[status as InspectionStatus].label.toLocaleLowerCase('pt-BR');
    return (
      <div className={`${barCls} flex items-center gap-2 text-xs text-muted-foreground`}>
        <Link2 className="h-3.5 w-3.5" />Link externo encerrado: vistoria {finalStatusLabel}
      </div>
    );
  }

  // Erro na consulta: mensagem e nova tentativa (evita carregamento infinito)
  if (isError) {
    return (
      <div className={`${barCls} flex items-center gap-2 text-xs`}>
        <Link2 className="h-3.5 w-3.5 text-destructive" />
        <span className="text-muted-foreground">Não foi possível carregar o link externo</span>
        <Button size="sm" variant="outline" className="h-8 text-xs ml-auto" onClick={() => refetch()}>
          <RefreshCw className="h-3.5 w-3.5 mr-1" />Tentar novamente
        </Button>
      </div>
    );
  }

  // Esqueleto em vez de sumir durante o carregamento
  if (isLoading || !data) {
    return <div className={barCls}><Skeleton className="h-8 w-full" /></div>;
  }

  const link = data.link && !data.link.is_expired ? data.link : null;
  const url = link ? buildPublicChecklistUrl(link.token) : null;
  const busy = generate.isPending || revoke.isPending;
  const identityInstruction = data.identity_kind === 'email'
    ? 'confirme o e-mail do usuário responsável'
    : data.identity_owner === 'provider'
      ? 'confirme o CPF/CNPJ do prestador responsável'
      : 'confirme o CPF/CNPJ do proprietário do veículo';
  const shareText = url ? `Olá! Segue o link para realizar a vistoria #${code}. Para acessar, ${identityInstruction}: ${url}` : '';

  const copy = async () => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Link copiado');
    setTimeout(() => setCopied(false), 2000);
  };
  // Compartilhamento nativo do aparelho; sem suporte, copia o link
  const share = async () => {
    if (!url) return;
    if (navigator.share) {
      try { await navigator.share({ title: `Vistoria #${code}`, text: shareText, url }); } catch { /* cancelado */ }
    } else await copy();
  };
  const whatsapp = () => url && window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener');

  // Sem link ativo: linha discreta com o motivo e botão de gerar
  if (!link) {
    const reason = !executable
      ? 'Vistoria enviada para revisão: sem link externo ativo'
      : !data.has_access_identity
        ? (data.unavailable_reason || 'Defina um responsável com identidade válida para gerar o link externo')
        : canManage
          ? `Sem link externo ativo (validade padrão: ${data.validity_days} dias)`
          : 'Sem link externo ativo. Peça a um responsável para gerar';
    return (
      <div className={`${barCls} flex items-center gap-2 text-xs`}>
        <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-muted-foreground">{reason}</span>
        {canManage && (
          <Button size="sm" className="h-8 text-xs ml-auto" disabled={busy || !data.has_access_identity} onClick={() => generate.mutate()}>
            {generate.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Link2 className="h-3.5 w-3.5 mr-1" />}
            Gerar link
          </Button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="px-4 md:px-5 py-2.5 bg-muted/40 border-b shrink-0 flex flex-col lg:flex-row lg:items-center gap-2">
        {/* Somente leitura: seleção/cópia continuam válidas */}
        <Input readOnly tabIndex={-1} value={url!}
          className="h-8 text-[11px] font-mono flex-1 bg-muted/60 cursor-default select-all focus-visible:ring-0 focus-visible:ring-offset-0" />
        <div className="flex items-center gap-1.5 shrink-0">
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={copy}>
            {copied ? <Check className="h-3.5 w-3.5 mr-1" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
            {copied ? 'Copiado' : 'Copiar'}
          </Button>
          <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => window.open(url!, '_blank', 'noopener,noreferrer')}>
            <ExternalLink className="h-3.5 w-3.5 mr-1" />Abrir
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 text-xs" disabled={busy}>
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <MoreHorizontal className="h-3.5 w-3.5 mr-1" />}Ações
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={share}><Share2 className="h-4 w-4 mr-2" />Compartilhar</DropdownMenuItem>
              <DropdownMenuItem onClick={whatsapp}><MessageCircle className="h-4 w-4 mr-2" />Enviar por WhatsApp</DropdownMenuItem>
              {canManage && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setConfirm('regen')}><RefreshCw className="h-4 w-4 mr-2" />Regerar link</DropdownMenuItem>
                  <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setConfirm('revoke')}>
                    <Ban className="h-4 w-4 mr-2" />Revogar link
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <p className="hidden xl:block text-[10px] text-muted-foreground leading-tight max-w-[300px]">
          {!executable && <span className="block font-medium">Vistoria enviada para revisão: o link fica em modo leitura.</span>}
          Link da vistoria — válido até {format(new Date(link.expires_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })} ou até a vistoria ser concluída/cancelada.
        </p>
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === 'regen' ? 'Regerar link?' : 'Revogar link?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === 'regen'
                ? 'Um novo link será criado e o atual deixará de funcionar imediatamente.'
                : 'O link atual deixará de funcionar imediatamente. Você poderá gerar outro depois.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className={confirm === 'revoke' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : undefined}
              onClick={() => { if (confirm === 'regen') generate.mutate(); else revoke.mutate(); setConfirm(null); }}>
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
